import type { FC, PropsWithChildren } from "react";
import { useMemo } from "react";

import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { Button } from "@jti/core-components/components/Button";
import { useProductDetails } from "@jti/magento";
import { ProductTryNowButton } from "@jti/ui/components/ProductTryNowButton";
import { Txt } from "@jti/ui/components/Txt";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { ProductPrice } from "@product-details/components/ProductPrice";

import { ProductPerks } from "./product-perks/ProductPerks";

import styles from "./ComprehensiveProductCard.module.scss";

const { sanitize } = dompurify;

type ColorVariant = {
  label: string;
  value: string;
};

export type IndividualProductCardProps = {
  hideLeftBadge: boolean;
  leftBadge?: string;
  leftBadgeIcon?: string;
  leftBadgeDark: boolean;
  hideRightBadge: boolean;
  rightBadge?: string;
  rightBadgeIcon?: string;
  rightBadgeDark: boolean;
  hidePriceInformation: boolean;
  hideStrikethroughPrice: boolean;
  hideDiscountBadge: boolean;
  hidePrice: boolean;
  hideBuyCTA: boolean;
  freeTrialProduct: boolean;
  hideDiscoverButton: boolean;
  hideDescription: boolean;
  discoverPageLink?: string;
  productPageLink: string;
  priceInformation?: string;
  priceInformationIcon?: string;
  sticksData: string; // JSON string with specific nested structure
  buyNowLabel?: string;
  discoverLabel?: string;
  tryNowLabel?: string;
  productData: {
    sku: string;
    name: string;
    shortDescription?: string;
    imageSrc: string;
    imageAlt?: string;
    url: string;
    colorVariantsStringified: string;
    bundle: boolean;
  };
};

/**
 * @deprecated
 * use ProductCard component from @jti/ui instead
 */

export const ComprehensiveProductCard: FC<PropsWithChildren<IndividualProductCardProps>> = ({
  hideLeftBadge,
  leftBadge,
  leftBadgeIcon,
  leftBadgeDark,
  hideRightBadge,
  rightBadge,
  rightBadgeIcon,
  rightBadgeDark,
  hidePriceInformation,
  hideStrikethroughPrice,
  hideDiscountBadge,
  hidePrice,
  hideBuyCTA,
  freeTrialProduct,
  hideDiscoverButton,
  hideDescription,
  discoverPageLink,
  productPageLink,
  priceInformation,
  priceInformationIcon,
  sticksData,
  discoverLabel,
  tryNowLabel,
  productData,
}) => {
  const {
    sku,
    name: productName,
    shortDescription,
    imageSrc,
    imageAlt,
    url,
    colorVariantsStringified,
    bundle: isBundle,
  } = productData;

  const { __ } = useTranslations();

  const colorVariants: ColorVariant[] = useMemo(() => {
    try {
      return JSON.parse(colorVariantsStringified);
    } catch {
      return [];
    }
  }, [colorVariantsStringified]);

  const parsedSticksData = useMemo(() => {
    try {
      return JSON.parse(sticksData);
    } catch {
      return {};
    }
  }, [sticksData]);

  const {
    isLoading: isProductDataLoading,
    discount,
    priceInformation: productPriceInfo,
  } = useProductDetails({
    sku,
    pageSize: 1,
    currentVariantSku: sku,
  });

  const cardClickHandler = () => {
    window.location.href = productPageLink;
  };

  const showLeftBadge = (leftBadge || leftBadgeIcon) && !hideLeftBadge;
  const showRightBadge = (rightBadge || rightBadgeIcon) && !hideRightBadge;
  const showPriceInfo = (!!priceInformation || !!priceInformationIcon) && !hidePriceInformation;
  const showBuyCTA = !hideBuyCTA && !freeTrialProduct;
  const showTryNowButton = freeTrialProduct && tryNowLabel;
  const showDiscoverButton = !hideDiscoverButton && discoverLabel && discoverPageLink;
  const showDiscountBadge = !hideDiscountBadge && discount;

  return (
    // Light theme is enforced here until the design system is updated
    <div className={classNames([styles.card, "light-theme"])} data-testid="product-card" onClick={cardClickHandler}>
      <div className={styles.header}>
        <div className={styles.badgeContainer}>
          <div
            className={classNames({
              [styles.badge]: showLeftBadge,
              [styles.darkBadge]: leftBadgeDark && showLeftBadge,
            })}
          >
            {showLeftBadge && (
              <>
                {leftBadgeIcon && <img className={styles.badgeIcon} src={leftBadgeIcon} alt="" />}
                <Txt className={styles.badgeText}>{leftBadge}</Txt>
              </>
            )}
          </div>
          {showRightBadge && (
            <div className={classNames({ [styles.badge]: true, [styles.darkBadge]: rightBadgeDark })}>
              {rightBadgeIcon && <img className={styles.badgeIcon} src={rightBadgeIcon} alt="" />}
              <Txt className={styles.badgeText}>{rightBadge}</Txt>
            </div>
          )}
        </div>
      </div>
      <div className={styles.imageWrapper}>
        <img src={imageSrc} alt={imageAlt ?? ""} />
      </div>
      <div className={styles.textWrapper}>
        <div className={styles.title}>{productName}</div>
        {!hideDescription && shortDescription && (
          <div className={styles.description} dangerouslySetInnerHTML={{ __html: sanitize(shortDescription) }} />
        )}
      </div>
      {!hidePrice && (
        <div className={styles.price}>
          <ProductPrice
            hideDiscountBadge
            hideStrikethroughPrice={hideStrikethroughPrice}
            priceInformation={productPriceInfo}
            discount={discount}
            isFetching={isProductDataLoading}
            priceClass={styles.innerPrice}
          />
        </div>
      )}
      {showPriceInfo && (
        <div className={styles.priceInfo} data-testid="price-information">
          {priceInformationIcon && <img src={priceInformationIcon} alt="" />}
          {priceInformation && <div>{priceInformation}</div>}
        </div>
      )}
      <div className={styles.buttonsWrapper}>
        <div data-testid="buttons" className={styles.buttons}>
          {showBuyCTA && (
            <Button href={productPageLink || url} variant="primary" nativeBehaviour>
              {__("product.button.buynow")}
            </Button>
          )}
          {showTryNowButton && (
            <ProductTryNowButton sku={sku} label={tryNowLabel} isBundle={isBundle} productPageUrl={productPageLink} />
          )}
          {showDiscoverButton && (
            <Button variant="secondary" href={discoverPageLink}>
              {discoverLabel}
            </Button>
          )}
        </div>
      </div>

      <div className={styles.footer}>
        <div>
          {colorVariants && (
            <ProductPerks
              configurableProductVariants={colorVariants}
              sticksData={parsedSticksData}
              productSku={sku}
              dataTestId="product-perks"
            />
          )}
        </div>
        {showDiscountBadge && (
          <Txt
            dataTestId="discount"
            className={classNames([styles.badge, styles.badgeText, styles.darkBadge])}
          >{`-${discount}%`}</Txt>
        )}
      </div>
    </div>
  );
};
